import { kosztKonstrukcji } from '../lib/konstrukcja'
import { kosztMontazu, stawkaMontazu } from '../config/cennikMontazu'
import { montazMagazynuZProgow, przeliczWycene, stawkaMontazuZProgow } from '../lib/wycena'
import useUstawieniaWyceny from './useUstawieniaWyceny'

/**
 * Rachunek zestawu w JEDNYM miejscu — kwota końcowa i jej składniki.
 *
 * DLACZEGO OSOBNY HAK: tę samą kwotę pokazuje karta zestawu (z rozbiciem)
 * i ostatnia strona dokumentu dla klienta (bez rozbicia). Policzona dwa razy,
 * prędzej czy później rozjechałaby się o stawkę montażu albo marżę — a to
 * znaczy dwie różne „ceny końcowe" tego samego zestawu, jedna na ekranie firmy
 * i druga w PDF-ie u klienta.
 *
 * Uwzględnia ustawienia konta firmowego (własne stawki montażu, marża, VAT).
 * Dla gościa i konta prywatnego liczy dokładnie tak, jak serwis liczył zawsze.
 *
 * Magazyn (runda 6): `kwh` to pojemność magazynu zestawu, `magazynZl` cena magazynów (już zawarta w `produktyZl`,
 * tylko podstawa marży procentowej). Z ustawieniami firmy dochodzi montaż magazynu z jej tabeli (zł/kWh) i marża
 * za magazyn; bez ustawień nic się nie zmienia.
 */
const useKosztZestawu = ({
  produktyZl,
  bmsZl = null,
  dostawaZl = null,
  kwp,
  mocModuluW,
  konstrukcja,
  montazWliczony = true,
  kwh = null,
  magazynZl = 0,
}) => {
  const wycena = useUstawieniaWyceny()

  const wKonstrukcja = kwp ? kosztKonstrukcji({ kwp, konstrukcja, mocModuluW }) : null

  // Stawka montażu: własna tabela firmy ma pierwszeństwo przed naszym cennikiem.
  // Gdy firma zostawi próg pusty, wracamy do wartości poglądowej — brak wpisanej
  // stawki nie może znaczyć „montaż za darmo".
  const stawkaFirmy = wycena ? stawkaMontazuZProgow(wycena.install_ranges, kwp) : null
  const stawkaZlZaKwp = stawkaFirmy != null ? stawkaFirmy : stawkaMontazu(kwp)
  const zlMontaz = montazWliczony && kwp
    ? (stawkaFirmy != null ? Math.round(stawkaFirmy * Number(kwp)) : kosztMontazu(kwp))
    : null

  // Montaż magazynu tylko z tabeli firmy i tylko razem z montażem instalacji (zestaw „bez montażu” to sam sprzęt).
  const montazMagazynu = wycena && montazWliczony && Number(kwh) > 0
    ? montazMagazynuZProgow(wycena.storage_install_ranges, kwh)
    : null

  const zrodloweZl = (produktyZl || 0) + (bmsZl || 0) + (dostawaZl || 0)
    + (wKonstrukcja?.razemZl || 0) + (zlMontaz || 0)

  // Składniki idą do rachunku ROZDZIELONE, bo mają różne stawki VAT: sprzęt,
  // dostawa i konstrukcja to towar (23%), montaż to usługa (8%).
  const przeliczone = wycena
    ? przeliczWycene({
      sprzetZl: (produktyZl || 0) + (bmsZl || 0),
      dostawaZl: dostawaZl || 0,
      konstrukcjaZl: wKonstrukcja?.razemZl || 0,
      montazZl: zlMontaz || 0,
      kwp,
      progiMarzy: wycena.margin_ranges,
      trybMarzy: wycena.margin_mode,
      vatSprzetu: wycena.source_vat_rate,
      vatMontazu: wycena.vat_rate,
      kwh,
      magazynZl: magazynZl || 0,
      montazMagazynuZl: montazMagazynu?.kwotaZl || 0,
      progiMarzyMagazynu: wycena.storage_margin_ranges,
    })
    : null

  return {
    wycena,
    wKonstrukcja,
    stawkaZlZaKwp,
    zlMontaz,
    // Montaż magazynu (kwota i stawka zł/kWh) albo null: bez magazynu, bez ustawień firmy albo bez stawki.
    zlMontazMagazynu: montazMagazynu?.kwotaZl ?? null,
    stawkaMagazynuZlZaKwh: montazMagazynu?.stawka ?? null,
    razemZl: przeliczone ? przeliczone.bruttoZl : zrodloweZl,
    // Netto znamy tylko z ustawień firmy (jej stawki VAT); bez nich dokument pokazuje samo brutto.
    nettoZl: przeliczone ? Math.round(przeliczone.nettoZl) : null,
    przeliczone,
    // Marża do osobnego wiersza w podsumowaniu na karcie zestawu (firma ma
    // widzieć, ile dokłada). Na dokumencie dla klienta jej NIE pokazujemy.
    marzaZl: przeliczone?.marzaZl || 0,
    // Stawkę montażu podajemy dalej TYLKO wtedy, gdy różni się od stawki
    // sprzętu — przy równych ta informacja niczego nie wnosi.
    stawkaVat: przeliczone && Number(wycena.vat_rate) !== Number(wycena.source_vat_rate)
      ? przeliczone.stawkaVatMontazu
      : null,
  }
}

export default useKosztZestawu
